import React from 'react'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useMemo } from 'modules/core/hooks/useMemo'
import hasFeature from 'modules/core/lib/hasFeature'

import { useInhousePayroll } from 'modules/payroll_bundle/hooks/useInhousePayroll'
import {
  INCIDENCE_STATUSES,
  INHOUSE_INCIDENCE_STATUSES,
} from 'modules/payroll_bundle/lib/Incidences/Status'

import DiscardDialog from './_DiscardDialog'
import InhouseStatusesDialog from './_InhouseStatusesDialog'
import SendToBookeeperDialog from './_SendToBookkeeperDialog'
import { Props, UpdateResult } from './types'

/**
 * Wrapper component that will render a specific dialog based on the new status,
 * and provides a common sync state management solution.
 */
export const StatusChangeDialog: React.FC<Props> = (props) => {
  const [syncing, setSyncing] = React.useState(false)
  const [results, setResults] = React.useState<UpdateResult | undefined>(undefined)

  const { newStatus, onDismiss, onCommit } = props
  const { hasInhousePayroll } = useInhousePayroll()
  const hasProfessionals = hasFeature('factorial_professionals')

  const DialogComponent = useMemo(() => {
    if (newStatus === INCIDENCE_STATUSES.discarded) {
      return DiscardDialog
    }

    if (
      hasInhousePayroll &&
      (newStatus === INHOUSE_INCIDENCE_STATUSES.doing ||
        newStatus === INHOUSE_INCIDENCE_STATUSES.to_do ||
        newStatus === INHOUSE_INCIDENCE_STATUSES.done)
    ) {
      return InhouseStatusesDialog
    }

    if (hasProfessionals && newStatus === INCIDENCE_STATUSES.to_do) {
      return SendToBookeeperDialog
    }

    return null
  }, [newStatus, hasInhousePayroll, hasProfessionals])

  /**
   * Call the provided `onCommit` function, but also manage the sync and result
   * states used by wrapped dialogs.
   */
  const handleCommit = useUnsafeCallback(
    (ids: Set<string>, ignoreWarnings?: boolean): Promise<UpdateResult> => {
      setSyncing(true)
      return onCommit(ids, ignoreWarnings)
        .then((result) => {
          if (result.success && !result.errorMessages) {
            // A flash will signal success
            onDismiss()
          }
          setResults(result)
          return result
        })
        .finally(() => {
          setSyncing(false)
        })
    },
    [onCommit, onDismiss]
  )

  if (!DialogComponent) {
    return null
  }

  return <DialogComponent {...props} syncing={syncing} results={results} onCommit={handleCommit} />
}
